import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Shield, Plus, Edit2, Trash2, Save, Users, Layers,
  Lock, Unlock, Check, Crown, UserCog, Eye
} from 'lucide-react';
import { useRBAC } from '@/contexts/RBACContext';

const API = process.env.REACT_APP_BACKEND_URL;

const EnhancedRoleManagement = () => {
  // Backend clears both permission + navigation caches on any RBAC write,
  // but the CURRENT admin's own sidebar navigation is held in React state
  // by RBACContext and won't re-fetch until page mount. When admins change
  // their own role's permissions here (or bulk-edit a role they hold),
  // their sidebar wouldn't reflect the new state until logout/login without
  // this hook. Calling refreshNavigation() after every save triggers an
  // immediate re-fetch. Note: OTHER users still need to log out+in to see
  // the change in THEIR own sessions — their browsers can't be pushed to.
  const { refreshNavigation } = useRBAC();
  const [roles, setRoles] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  
  // Role dialog state
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    role_code: '',
    role_name: '',
    description: '',
    level: 50,
    status: 'active'
  });
  
  // Bulk assign state
  const [bulkAssignGroups, setBulkAssignGroups] = useState([]);
  
  // Assignment state
  const [assignedGroups, setAssignedGroups] = useState([]);
  const [activeTab, setActiveTab] = useState('groups');

  const token = sessionStorage.getItem('auth_token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesRes, groupsRes, permsRes] = await Promise.all([
        axios.get(`${API}/api/admin/rbac-roles?token=${token}&include_inactive=true`),
        axios.get(`${API}/api/admin/permission-groups?token=${token}`),
        axios.get(`${API}/api/admin/permissions/catalog?token=${token}`)
      ]);
      
      if (rolesRes.data.success) {
        setRoles(rolesRes.data.roles);
        if (rolesRes.data.roles.length > 0 && !selectedRole) {
          selectRole(rolesRes.data.roles[0]);
        }
      }
      
      if (groupsRes.data.success) {
        setPermissionGroups(groupsRes.data.groups);
      }
      
      if (permsRes.data.success) {
        setPermissions(permsRes.data.permissions);
      }
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const selectRole = (role) => {
    setSelectedRole(role);
    setAssignedGroups((role.permission_groups || []).map(g => g.id));
  };

  const getRoleBadgeColor = (role) => {
    if (role.is_system) return 'bg-amber-100 text-amber-800';
    if (role.level <= 2) return 'bg-red-100 text-red-800';
    if (role.level <= 4) return 'bg-blue-100 text-blue-800';
    return 'bg-slate-100 text-slate-800';
  };

  const getRoleIcon = (role) => {
    if (role.role_code === 'super_admin') return Crown;
    if (role.is_system) return Lock;
    return Shield;
  };

  // Role CRUD
  const openRoleDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        role_code: role.role_code,
        role_name: role.role_name,
        description: role.description || '',
        level: role.level,
        status: role.status
      });
    } else {
      setEditingRole(null);
      setRoleForm({
        role_code: '',
        role_name: '',
        description: '',
        level: 50,
        status: 'active'
      });
    }
    setShowRoleDialog(true);
  };

  const saveRole = async () => {
    try {
      if (editingRole) {
        await axios.put(`${API}/api/admin/rbac-roles/${editingRole.id}?token=${token}`, roleForm);
        toast.success('Role updated');
      } else {
        await axios.post(`${API}/api/admin/rbac-roles?token=${token}`, roleForm);
        toast.success('Role created');
      }
      setShowRoleDialog(false);
      fetchData();
      refreshNavigation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  // Group assignment
  const toggleGroup = (groupId) => {
    setAssignedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const saveRoleGroups = async () => {
    if (!selectedRole) return;

    try {
      await axios.put(
        `${API}/api/admin/rbac-roles/${selectedRole.id}/groups?token=${token}`,
        { group_ids: assignedGroups }
      );
      // Tell the admin that OTHER users with this role need to log out+in
      // to see the change in their own browsers — we can't push nav to
      // them, only invalidate the backend cache (already done server-side).
      toast.success(
        `Permission groups assigned to "${selectedRole.role_name || selectedRole.role_code}". ` +
        `Users with this role must log out and log back in to see the change.`,
      );
      fetchData();
      refreshNavigation();
    } catch (error) {
      toast.error('Failed to assign groups');
    }
  };

  // Delete role
  const deleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Are you sure you want to delete the role "${roleName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/admin/rbac-roles/${roleId}?token=${token}`);
      toast.success('Role deleted successfully');
      if (selectedRole?.id === roleId) {
        setSelectedRole(null);
      }
      fetchData();
      refreshNavigation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  // Multi-select handlers
  const toggleRoleSelection = (roleId, e) => {
    e.stopPropagation();
    setSelectedRoleIds(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const selectAllRoles = () => {
    const selectableRoles = roles.filter(r => r.role_code !== 'super_admin').map(r => r.id);
    setSelectedRoleIds(selectableRoles);
  };

  const clearSelection = () => {
    setSelectedRoleIds([]);
  };

  const openBulkAssignDialog = () => {
    setBulkAssignGroups([]);
    setShowBulkAssignDialog(true);
  };

  const toggleBulkGroup = (groupId) => {
    setBulkAssignGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const saveBulkAssignment = async () => {
    if (selectedRoleIds.length === 0 || bulkAssignGroups.length === 0) {
      toast.error('Select at least one role and one permission group');
      return;
    }

    try {
      let successCount = 0;
      for (const roleId of selectedRoleIds) {
        const role = roles.find(r => r.id === roleId);
        if (role && role.role_code !== 'super_admin') {
          // Merge existing groups with new ones
          const existingGroups = (role.permission_groups || []).map(g => g.id);
          const mergedGroups = [...new Set([...existingGroups, ...bulkAssignGroups])];
          
          await axios.put(
            `${API}/api/admin/rbac-roles/${roleId}/groups?token=${token}`,
            { group_ids: mergedGroups }
          );
          successCount++;
        }
      }
      toast.success(
        `Updated ${successCount} roles. Users with these roles must log out and log back in to see the change.`,
      );
      setShowBulkAssignDialog(false);
      setSelectedRoleIds([]);
      fetchData();
      refreshNavigation();
    } catch (error) {
      toast.error('Failed to bulk assign groups');
    }
  };

  // Calculate effective permissions for selected role
  const getEffectivePermissions = () => {
    if (!selectedRole) return {};
    
    const effective = {};
    
    // From assigned groups
    assignedGroups.forEach(groupId => {
      const group = permissionGroups.find(g => g.id === groupId);
      if (group) {
        (group.items || []).forEach(item => {
          // Items already have permission_code from the API
          const permCode = item.permission_code;
          if (permCode) {
            if (!effective[permCode]) {
              effective[permCode] = new Set();
            }
            (item.allowed_actions || []).forEach(a => effective[permCode].add(a));
          }
        });
      }
    });
    
    // From direct permissions on the role
    (selectedRole.direct_permissions || []).forEach(item => {
      const permCode = item.permission_code;
      if (permCode) {
        if (!effective[permCode]) {
          effective[permCode] = new Set();
        }
        (item.allowed_actions || []).forEach(a => effective[permCode].add(a));
      }
    });
    
    return effective;
  };

  const effectivePermissions = getEffectivePermissions();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="enhanced-role-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Role Management</h1>
          <p className="text-slate-600">Define roles and assign permission groups</p>
        </div>
        <Button onClick={() => openRoleDialog()} data-testid="add-role-btn">
          <Plus size={16} className="mr-2" />
          Add Role
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Roles List */}
        <div className="col-span-4">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Shield size={16} />
                Roles
              </h3>
              {selectedRoleIds.length > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{selectedRoleIds.length} selected</Badge>
                  <Button variant="outline" size="sm" onClick={clearSelection}>Clear</Button>
                </div>
              )}
            </div>
            
            {/* Bulk Actions Bar */}
            {selectedRoleIds.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-700">{selectedRoleIds.length} role(s) selected</span>
                  <Button size="sm" onClick={openBulkAssignDialog} className="bg-blue-600 hover:bg-blue-700">
                    <Layers size={14} className="mr-1" />
                    Assign Groups
                  </Button>
                </div>
              </div>
            )}
            
            {/* Select All */}
            <div className="flex items-center gap-2 mb-3 pb-3 border-b">
              <Checkbox
                checked={selectedRoleIds.length === roles.filter(r => r.role_code !== 'super_admin').length}
                onCheckedChange={(checked) => checked ? selectAllRoles() : clearSelection()}
              />
              <span className="text-sm text-slate-600">Select All (except Super Admin)</span>
            </div>
            
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {roles.map((role) => {
                const RoleIcon = getRoleIcon(role);
                const isSelected = selectedRoleIds.includes(role.id);
                return (
                  <div
                    key={role.id}
                    onClick={() => selectRole(role)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedRole?.id === role.id
                        ? 'border-primary bg-primary/5'
                        : isSelected
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {role.role_code !== 'super_admin' && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => {}}
                            onClick={(e) => toggleRoleSelection(role.id, e)}
                          />
                        )}
                        <RoleIcon size={16} className={role.is_system ? 'text-amber-500' : 'text-slate-500'} />
                        <div>
                          <div className="font-medium">{role.role_name}</div>
                          <div className="text-xs text-slate-500 font-mono">{role.role_code}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge className={`text-xs ${getRoleBadgeColor(role)}`}>
                          L{role.level}
                        </Badge>
                        {role.role_code !== 'super_admin' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openRoleDialog(role);
                              }}
                              title="Edit Role"
                            >
                              <Edit2 size={12} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteRole(role.id, role.role_name);
                              }}
                              title="Delete Role"
                            >
                              <Trash2 size={12} />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    {role.description && (
                      <p className="text-xs text-slate-500 mt-1 ml-6">{role.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 ml-6">
                      <Badge variant="outline" className="text-xs">
                        {role.permission_groups?.length || 0} groups
                      </Badge>
                      {role.is_system && (
                        <Badge variant="secondary" className="text-xs">
                          <Lock size={10} className="mr-1" />
                          System
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Role Configuration */}
        <div className="col-span-8">
          <Card className="p-4">
            {selectedRole ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold flex items-center gap-2">
                      <UserCog size={16} />
                      Configure: {selectedRole.role_name}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {selectedRole.role_code === 'super_admin' 
                        ? 'Super Admin has all permissions by default'
                        : 'Assign permission groups to define access for this role'}
                    </p>
                  </div>
                  {selectedRole.role_code !== 'super_admin' && (
                    <Button onClick={saveRoleGroups} data-testid="save-role-groups-btn">
                      <Save size={14} className="mr-2" />
                      Save Changes
                    </Button>
                  )}
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="mb-4">
                    <TabsTrigger value="groups" className="flex items-center gap-1.5">
                      <Layers size={14} />
                      Permission Groups
                    </TabsTrigger>
                    <TabsTrigger value="effective" className="flex items-center gap-1.5">
                      <Eye size={14} />
                      Effective Permissions
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="groups">
                    {selectedRole.role_code === 'super_admin' ? (
                      <div className="text-center py-8 bg-amber-50 rounded-lg border border-amber-200">
                        <Crown size={32} className="mx-auto mb-2 text-amber-500" />
                        <p className="font-medium text-amber-800">Super Admin</p>
                        <p className="text-sm text-amber-600">Has all permissions by default</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3 max-h-[500px] overflow-y-auto">
                        {permissionGroups.map((group) => {
                          const isAssigned = assignedGroups.includes(group.id);
                          return (
                            <div
                              key={group.id}
                              onClick={() => toggleGroup(group.id)}
                              className={`p-4 rounded-lg border cursor-pointer transition-all ${
                                isAssigned
                                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                  : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  checked={isAssigned}
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{group.group_name}</div>
                                  <div className="text-xs text-slate-500">{group.description}</div>
                                  <div className="flex items-center gap-1 mt-1">
                                    <Badge variant="outline" className="text-xs">
                                      {group.items?.length || 0} permissions
                                    </Badge>
                                  </div>
                                </div>
                                {isAssigned && (
                                  <Check size={16} className="text-primary" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="effective">
                    <div className="space-y-4 max-h-[500px] overflow-y-auto">
                      {selectedRole.role_code === 'super_admin' ? (
                        <div className="text-center py-8 bg-amber-50 rounded-lg border border-amber-200">
                          <Crown size={32} className="mx-auto mb-2 text-amber-500" />
                          <p className="font-medium text-amber-800">All Permissions Granted</p>
                          <p className="text-sm text-amber-600">Super Admin has full access to all features</p>
                        </div>
                      ) : Object.keys(effectivePermissions).length > 0 ? (
                        Object.entries(
                          Object.entries(effectivePermissions).reduce((acc, [code, actions]) => {
                            const menu = code.split('.')[0];
                            if (!acc[menu]) acc[menu] = [];
                            acc[menu].push({ code, actions: Array.from(actions) });
                            return acc;
                          }, {})
                        ).map(([menu, perms]) => (
                          <div key={menu} className="border rounded-lg p-3">
                            <h4 className="font-medium text-sm text-slate-700 mb-2 uppercase">
                              {menu.replace(/_/g, ' ')}
                            </h4>
                            <div className="space-y-1">
                              {perms.map(({ code, actions }) => (
                                <div key={code} className="flex items-center justify-between py-1">
                                  <span className="text-sm">{code.split('.').pop()}</span>
                                  <div className="flex gap-1">
                                    {actions.map(action => (
                                      <Badge key={action} variant="outline" className="text-xs">
                                        {action}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <Shield size={32} className="mx-auto mb-2 opacity-50" />
                          <p>No permissions assigned</p>
                          <p className="text-sm">Assign permission groups to grant access</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </>
            ) : (
              <div className="text-center py-16 text-slate-500">
                <Shield size={48} className="mx-auto mb-4 opacity-30" />
                <p>Select a role to configure</p>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-w-md" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Add Role'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Role Code</Label>
              <Input
                value={roleForm.role_code}
                onChange={(e) => setRoleForm({ ...roleForm, role_code: e.target.value })}
                placeholder="e.g., sales_manager"
                disabled={!!editingRole}
              />
            </div>
            <div className="space-y-2">
              <Label>Role Name</Label>
              <Input
                value={roleForm.role_name}
                onChange={(e) => setRoleForm({ ...roleForm, role_name: e.target.value })}
                placeholder="Sales Manager"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                placeholder="Manages sales team and operations"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Level (0=highest)</Label>
                <Input
                  type="number"
                  value={roleForm.level}
                  onChange={(e) => setRoleForm({ ...roleForm, level: parseInt(e.target.value) })}
                  min={0}
                  max={99}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={roleForm.status}
                  onValueChange={(value) => setRoleForm({ ...roleForm, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveRole}>
              <Save size={14} className="mr-2" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={showBulkAssignDialog} onOpenChange={setShowBulkAssignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Assign Permission Groups</DialogTitle>
            <DialogDescription>
              Select permission groups to assign to {selectedRoleIds.length} selected role(s)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-[400px] overflow-y-auto space-y-2">
            {permissionGroups.map((group) => {
              const isSelected = bulkAssignGroups.includes(group.id);
              return (
                <div
                  key={group.id}
                  onClick={() => toggleBulkGroup(group.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    isSelected
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox checked={isSelected} />
                    <div>
                      <div className="font-medium">{group.group_name}</div>
                      <div className="text-xs text-slate-500">{group.description}</div>
                      <div className="text-xs text-slate-400 mt-1">
                        {group.permissions?.length || 0} permissions
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveBulkAssignment} disabled={bulkAssignGroups.length === 0}>
              <Save size={14} className="mr-2" />
              Assign to {selectedRoleIds.length} Role(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancedRoleManagement;
